import { useState } from 'react';
import { Link } from 'react-router-dom';
import { Button } from '../components/ui/button';
import { Input } from '../components/ui/input';
import { Label } from '../components/ui/label';
import { toast } from 'sonner';
import { Envelope, User, Sparkle, CheckCircle, Clock, Users, Star } from '@phosphor-icons/react';
import { motion } from 'framer-motion';
import axios from 'axios';

const API_URL = `${process.env.REACT_APP_BACKEND_URL}/api`;

export default function Register() {
  const [name, setName] = useState('');
  const [email, setEmail] = useState('');
  const [reason, setReason] = useState('');
  const [loading, setLoading] = useState(false);
  const [submitted, setSubmitted] = useState(false);

  const handleSubmit = async (e) => {
    e.preventDefault();
    setLoading(true);
    
    try {
      await axios.post(`${API_URL}/access-request`, { name, email, reason });
      setSubmitted(true);
      toast.success('Request submitted successfully!');
    } catch (error) {
      if (error.response?.data?.message?.includes('already submitted')) {
        toast.info('You have already submitted a request. We will be in touch soon!');
        setSubmitted(true);
      } else {
        toast.error('Failed to submit request. Please try again.');
      }
    } finally {
      setLoading(false);
    }
  };

  if (submitted) {
    return (
      <div className="min-h-screen bg-[#F8F6FF] flex items-center justify-center p-6">
        <motion.div 
          initial={{ opacity: 0, scale: 0.9 }}
          animate={{ opacity: 1, scale: 1 }}
          className="max-w-md w-full text-center"
        >
          <div className="glass-card rounded-3xl p-10">
            <div className="w-20 h-20 rounded-full bg-[#D1FAE5] flex items-center justify-center mx-auto mb-6">
              <CheckCircle className="w-10 h-10 text-[#059669]" weight="fill" />
            </div>
            <h1 className="text-3xl font-bold text-[#1E1B4B] font-['Outfit'] mb-4">
              You're on the List!
            </h1>
            <p className="text-[#6B7280] mb-6">
              Thank you for your interest in MenoPath. We've received your early access request and will be in touch soon.
            </p>
            <div className="bg-[#EDE9FE] rounded-2xl p-4 mb-8">
              <p className="text-sm text-[#5B21B6]">
                <strong>What happens next?</strong><br />
                Our team will review your request and send you an invite within 5-7 business days.
              </p>
            </div>
            <Link to="/">
              <Button className="bg-[#5B21B6] hover:bg-[#4C1D95] rounded-full px-8">
                Return to Home
              </Button>
            </Link>
          </div>
        </motion.div>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-[#F8F6FF] flex">
      {/* Left side - Branding */}
      <div className="hidden lg:flex lg:w-1/2 bg-gradient-to-br from-[#5B21B6] to-[#7C3AED] p-12 flex-col justify-between relative overflow-hidden">
        {/* Background decoration */}
        <div className="absolute top-20 right-20 w-64 h-64 bg-white/10 rounded-full blur-3xl" />
        <div className="absolute bottom-20 left-20 w-96 h-96 bg-white/10 rounded-full blur-3xl" />
        
        <div className="relative z-10">
          <Link to="/" className="text-white text-3xl font-bold font-['Outfit']">
            MenoPath
          </Link>
          <p className="text-white/80 mt-2">Science-driven Actionable Insights</p>
        </div>
        
        <div className="space-y-8 relative z-10">
          {/* Beta Badge */}
          <motion.div
            initial={{ opacity: 0, y: 20 }}
            animate={{ opacity: 1, y: 0 }}
            className="inline-flex items-center gap-2 bg-white/20 backdrop-blur-sm px-4 py-2 rounded-full"
          >
            <Sparkle className="w-5 h-5 text-yellow-300" weight="fill" />
            <span className="text-white font-medium">Limited Beta Access</span>
          </motion.div>
          
          <motion.h2 
            initial={{ opacity: 0, y: 20 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ delay: 0.1 }}
            className="text-4xl font-bold text-white font-['Outfit'] leading-tight"
          >
            Be Among the<br />First to Experience<br />MenoPath
          </motion.h2>
          
          <motion.p
            initial={{ opacity: 0, y: 20 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ delay: 0.2 }}
            className="text-white/80 text-lg max-w-md"
          >
            We're carefully onboarding users during our beta phase to ensure the best experience. Request access and join our growing community.
          </motion.p>
          
          {/* Stats */}
          <motion.div 
            initial={{ opacity: 0, y: 20 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ delay: 0.3 }}
            className="flex gap-8"
          >
            <div className="flex items-center gap-3">
              <div className="w-12 h-12 rounded-xl bg-white/20 flex items-center justify-center">
                <Users className="w-6 h-6 text-white" weight="duotone" />
              </div>
              <div>
                <p className="text-2xl font-bold text-white">8,000+</p>
                <p className="text-white/60 text-sm">Beta Users</p>
              </div>
            </div>
            <div className="flex items-center gap-3">
              <div className="w-12 h-12 rounded-xl bg-white/20 flex items-center justify-center">
                <Star className="w-6 h-6 text-white" weight="duotone" />
              </div>
              <div>
                <p className="text-2xl font-bold text-white">4.9/5</p>
                <p className="text-white/60 text-sm">User Rating</p>
              </div>
            </div>
          </motion.div>
        </div>
        
        <p className="text-white/50 text-sm relative z-10">
          Invites sent within 5-7 business days
        </p>
      </div>
      
      {/* Right side - Request Access form */}
      <div className="flex-1 flex items-center justify-center p-8">
        <motion.div 
          initial={{ opacity: 0, x: 20 }}
          animate={{ opacity: 1, x: 0 }}
          className="w-full max-w-md"
        >
          <div className="lg:hidden mb-8">
            <Link to="/" className="text-[#5B21B6] text-2xl font-bold font-['Outfit']">
              MenoPath
            </Link>
            <div className="inline-flex items-center gap-2 bg-[#EDE9FE] px-3 py-1 rounded-full ml-3">
              <Sparkle className="w-4 h-4 text-[#5B21B6]" weight="fill" />
              <span className="text-[#5B21B6] text-sm font-medium">Beta</span>
            </div>
          </div>
          
          <div className="flex items-center gap-2 mb-2">
            <Clock className="w-5 h-5 text-[#5B21B6]" weight="duotone" />
            <span className="text-sm text-[#5B21B6] font-medium">Limited spots available</span>
          </div>
          
          <h1 className="text-3xl font-bold text-[#1E1B4B] font-['Outfit']">Request Early Access</h1>
          <p className="text-[#6B7280] mt-2">
            We're in beta and can only process a limited number of users at this time. Submit your request to join the waitlist.
          </p>
          
          {/* Beta Notice */}
          <div className="mt-6 bg-gradient-to-r from-[#FEF3C7] to-[#FDE68A] rounded-2xl p-4 border border-[#F59E0B]/20">
            <div className="flex items-start gap-3">
              <Sparkle className="w-5 h-5 text-[#92400E] flex-shrink-0 mt-0.5" weight="fill" />
              <div>
                <p className="text-sm text-[#92400E] font-medium">Why Early Access?</p>
                <p className="text-sm text-[#92400E]/80 mt-1">
                  As a beta user, you'll help shape MenoPath's future while getting exclusive access to our AI-powered menopause predictions before the public launch.
                </p>
              </div>
            </div>
          </div>
          
          <form onSubmit={handleSubmit} className="mt-8 space-y-5">
            <div className="space-y-2">
              <Label htmlFor="name" className="text-[#1E1B4B]">Full Name</Label>
              <div className="relative">
                <User className="absolute left-4 top-1/2 -translate-y-1/2 text-[#6B7280] w-5 h-5" weight="duotone" />
                <Input
                  id="name"
                  type="text"
                  value={name}
                  onChange={(e) => setName(e.target.value)}
                  placeholder="Enter your name"
                  className="pl-12 h-12 rounded-xl border-2 border-gray-200 focus:border-[#5B21B6] focus:ring-[#5B21B6]/20"
                  data-testid="register-name-input"
                  required
                />
              </div>
            </div>
            
            <div className="space-y-2">
              <Label htmlFor="email" className="text-[#1E1B4B]">Email Address</Label>
              <div className="relative">
                <Envelope className="absolute left-4 top-1/2 -translate-y-1/2 text-[#6B7280] w-5 h-5" weight="duotone" />
                <Input
                  id="email"
                  type="email"
                  value={email}
                  onChange={(e) => setEmail(e.target.value)}
                  placeholder="Enter your email"
                  className="pl-12 h-12 rounded-xl border-2 border-gray-200 focus:border-[#5B21B6] focus:ring-[#5B21B6]/20"
                  data-testid="register-email-input"
                  required
                />
              </div>
            </div>
            
            <div className="space-y-2">
              <Label htmlFor="reason" className="text-[#1E1B4B]">Why are you interested in MenoPath? <span className="text-[#6B7280] font-normal">(optional)</span></Label>
              <textarea
                id="reason"
                value={reason}
                onChange={(e) => setReason(e.target.value)}
                placeholder="Tell us a bit about yourself and why you'd like early access..."
                className="w-full px-4 py-3 h-24 rounded-xl border-2 border-gray-200 focus:border-[#5B21B6] focus:ring-2 focus:ring-[#5B21B6]/20 outline-none resize-none text-sm"
                data-testid="register-reason-input"
              />
            </div>
            
            <Button
              type="submit"
              disabled={loading}
              className="w-full h-12 bg-[#5B21B6] hover:bg-[#4C1D95] rounded-full text-base font-semibold"
              data-testid="register-submit-btn"
            >
              {loading ? (
                <div className="flex items-center gap-2">
                  <div className="w-5 h-5 border-2 border-white border-t-transparent rounded-full animate-spin" />
                  Submitting...
                </div>
              ) : (
                'Request Access'
              )}
            </Button>
          </form>
          
          <p className="text-center mt-6 text-[#6B7280] text-sm">
            Already have an invite?{' '}
            <Link to="/login" className="text-[#5B21B6] font-semibold hover:underline" data-testid="login-link">
              Sign in here
            </Link>
          </p>
          
          <p className="text-center mt-4 text-xs text-[#9CA3AF]">
            By requesting access, you agree to our{' '}
            <Link to="/terms" className="underline hover:text-[#5B21B6]">Terms of Service</Link>
            {' '}and{' '}
            <Link to="/privacy" className="underline hover:text-[#5B21B6]">Privacy Policy</Link>
          </p>
        </motion.div>
      </div>
    </div>
  );
}
